/**
 * 🔊 Serviço de Som para Chat Interno
 * 
 * Toca som quando:
 * 1. Nova mensagem chega na sala onde o participante está
 * 2. Participante recebe uma mensagem privada
 * 3. Tela do chat está recolhida/minimizada
 */

// Som específico do chat interno
const CHAT_SOUND_URL = "/sounds/beep_interno.ogg";

class ChatSoundService {
  constructor() {
    this.audioContext = null;
    this.audioBuffer = null;
    this.htmlAudio = null;
    this.isUnlocked = false;
    this.volume = 1;
    this.lastPlayTime = 0;
    this.minInterval = 800; // Intervalo mínimo entre sons (ms)
    
    // Inicializar ao carregar
    this.init();
  }

  /**
   * Inicializa o serviço de áudio
   */
  async init() {
    try {
      // Tentar criar Web Audio API Context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        this.audioContext = new AudioContext();
        await this.loadAudioBuffer();
      }
      
      // Criar elemento HTML Audio como fallback
      this.htmlAudio = new Audio(CHAT_SOUND_URL);
      this.htmlAudio.preload = "auto";
      this.htmlAudio.load();
      
      // Configurar desbloqueio de áudio em interações do usuário
      this.setupUnlock();
      
      console.log("🔔 [ChatSoundService] Inicializado com sucesso");
    } catch (err) {
      console.warn("⚠️ [ChatSoundService] Erro ao inicializar:", err);
    }
  }

  /**
   * Carrega o buffer de áudio para Web Audio API
   */
  async loadAudioBuffer() {
    try {
      const response = await fetch(CHAT_SOUND_URL);
      const arrayBuffer = await response.arrayBuffer();
      this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      console.log("🔔 [ChatSoundService] Buffer de áudio carregado");
    } catch (err) {
      console.warn("⚠️ [ChatSoundService] Erro ao carregar buffer:", err);
    }
  }

  /**
   * Configura desbloqueio de áudio em interações do usuário
   */
  setupUnlock() {
    const unlockAudio = async () => {
      if (this.isUnlocked) return;
      
      try {
        // Desbloquear Web Audio API
        if (this.audioContext && this.audioContext.state === "suspended") {
          await this.audioContext.resume();
        }
        
        // Desbloquear HTML Audio
        if (this.htmlAudio) {
          this.htmlAudio.muted = true;
          await this.htmlAudio.play().catch(() => {});
          this.htmlAudio.pause();
          this.htmlAudio.muted = false;
          this.htmlAudio.currentTime = 0;
        }
        
        this.isUnlocked = true;
        console.log("🔓 [ChatSoundService] Áudio desbloqueado");
        
        // Remover listeners após desbloquear
        document.removeEventListener("click", unlockAudio);
        document.removeEventListener("touchstart", unlockAudio);
        document.removeEventListener("keydown", unlockAudio);
      } catch (err) {
        console.warn("⚠️ [ChatSoundService] Erro ao desbloquear áudio:", err);
      }
    };

    document.addEventListener("click", unlockAudio);
    document.addEventListener("touchstart", unlockAudio);
    document.addEventListener("keydown", unlockAudio);
  }

  /**
   * Define o volume (0 a 1)
   */
  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.htmlAudio) {
      this.htmlAudio.volume = this.volume;
    }
  }

  /**
   * Toca o som de notificação do chat interno
   */
  async play() {
    // Evitar tocar sons muito frequentes
    const now = Date.now();
    if (now - this.lastPlayTime < this.minInterval) {
      console.log("🔇 [ChatSoundService] Som ignorado (muito frequente)");
      return false;
    }
    this.lastPlayTime = now;

    if (this.volume === 0) {
      console.log("🔇 [ChatSoundService] Volume em 0, som ignorado");
      return false;
    }

    let played = false;

    // 🔊 PRIORIDADE: HTML Audio Element primeiro (funciona melhor antes do user gesture)
    if (!played && this.htmlAudio) {
      played = await this.playWithHtmlAudio();
    }

    // Estratégia 2: Criar novo elemento Audio (fallback rápido)
    if (!played) {
      played = await this.playWithNewAudio();
    }

    // Estratégia 3: Web Audio API (último recurso - requer user gesture)
    if (!played && this.audioContext && this.audioBuffer) {
      if (this.audioContext.state === "suspended") {
        try {
          await this.audioContext.resume();
        } catch (e) {
          // Ignorar erro de resume
        }
      }
      if (this.audioContext.state === "running") {
        played = await this.playWithWebAudio();
      }
    }

    if (played) {
      console.log("🔔 [ChatSoundService] Som do chat tocado");
    } else {
      console.warn("⚠️ [ChatSoundService] Não foi possível tocar o som");
    }

    return played;
  }

  /**
   * Toca usando Web Audio API
   */
  async playWithWebAudio() {
    try {
      // Não tentar se o contexto estiver suspenso (evita warning)
      if (this.audioContext.state === "suspended") {
        return false;
      }

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = this.audioBuffer;
      gainNode.gain.value = this.volume;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start(0);
      return true;
    } catch (err) {
      // Silenciar erro de Web Audio - HTML Audio é o fallback principal
      return false;
    }
  }

  /**
   * Toca usando HTML Audio Element pré-carregado
   */
  async playWithHtmlAudio() {
    try {
      this.htmlAudio.currentTime = 0;
      this.htmlAudio.volume = this.volume;
      
      await this.htmlAudio.play();
      return true;
    } catch (err) {
      console.warn("⚠️ [ChatSoundService] Erro HTML Audio:", err);
      return false;
    }
  }

  /**
   * Toca criando um novo elemento Audio
   */
  async playWithNewAudio() {
    try {
      const audio = new Audio(CHAT_SOUND_URL);
      audio.volume = this.volume;
      
      await audio.play();
      
      // Limpar após tocar
      audio.addEventListener("ended", () => {
        audio.remove();
      });
      
      return true;
    } catch (err) {
      console.warn("⚠️ [ChatSoundService] Erro novo Audio:", err);
      return false;
    }
  }
}

// Singleton - uma única instância para toda a aplicação
const chatSoundService = new ChatSoundService();

export default chatSoundService;

